import React, { useEffect, useState } from 'react';
import BaseLayout from './BaseLayout';
import "../App.css";
import axios from 'axios';
import { API_BASE } from '../api';
import { useAuth } from '../context/AuthContext';
import { useView } from '../context/ViewContext';

// Teams arrays as module-level constants (avoids re-creation on every render)
const NFL_TEAMS = [
    "Arizona Cardinals", "Atlanta Falcons", "Baltimore Ravens", "Buffalo Bills",
    "Carolina Panthers", "Chicago Bears", "Cincinnati Bengals", "Cleveland Browns",
    "Dallas Cowboys", "Denver Broncos", "Detroit Lions", "Green Bay Packers",
    "Houston Texans", "Indianapolis Colts", "Jacksonville Jaguars", "Kansas City Chiefs",
    "Las Vegas Raiders", "Los Angeles Chargers", "Los Angeles Rams", "Miami Dolphins",
    "Minnesota Vikings", "New England Patriots", "New Orleans Saints", "New York Giants",
    "New York Jets", "Philadelphia Eagles", "Pittsburgh Steelers", "San Francisco 49ers",
    "Seattle Seahawks", "Tampa Bay Buccaneers", "Tennessee Titans", "Washington Commanders"
];

const NBA_TEAMS = [
    "Atlanta Hawks", "Boston Celtics", "Brooklyn Nets", "Charlotte Hornets",
    "Chicago Bulls", "Cleveland Cavaliers", "Dallas Mavericks", "Denver Nuggets",
    "Detroit Pistons", "Golden State Warriors", "Houston Rockets", "Indiana Pacers",
    "LA Clippers", "Los Angeles Lakers", "Memphis Grizzlies", "Miami Heat",
    "Milwaukee Bucks", "Minnesota Timberwolves", "New Orleans Pelicans", "New York Knicks",
    "Oklahoma City Thunder", "Orlando Magic", "Philadelphia 76ers", "Phoenix Suns",
    "Portland Trail Blazers", "Sacramento Kings", "San Antonio Spurs", "Toronto Raptors",
    "Utah Jazz", "Washington Wizards"
];

const AccountPage = () => {
    const { authData, updateUserAttribute } = useAuth();
    const { viewID } = useView();
    const [loading, setLoading] = useState(false);
    const [profilePicError, setProfilePicError] = useState('');
    const [userInfo, setUserInfo] = useState({
        username: '',
        profilePicture: '',
        points: '',
        favoriteNFLTeam: '',
        favoriteNBATeam: '',
        id: 0,
        rank: 0
    });

    // If viewing own account (viewID is empty) use authData; otherwise, fetch via API
    useEffect(() => {
        document.body.classList.add("account-page-bg");

        const fetchData = async () => {
            try {
                setLoading(true);
                const userId = authData.user?.id;
                // If viewID is set and not equal to own id, fetch that user
                const userInfoPromise = (viewID === '' || viewID === userId)
                    ? axios.get(`${API_BASE}/data/get-user/${userId}`)
                    : axios.get(`${API_BASE}/data/get-user/${viewID}`);

                const [userInfoResponse] = await Promise.all([userInfoPromise]);

                const fetchedUserInfo = {
                    username: userInfoResponse.data.firstName + " " + userInfoResponse.data.lastName,
                    profilePicture: userInfoResponse.data.profile_picture,
                    points: userInfoResponse.data.score,
                    favoriteNFLTeam: userInfoResponse.data.favorite_nfl_team,
                    favoriteNBATeam: userInfoResponse.data.favorite_nba_team,
                    id: userInfoResponse.data.id,
                    rank: userInfoResponse.data.rank
                };

                setUserInfo(fetchedUserInfo);
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
            }
        };

        if (viewID !== '' || authData.user?.profile_picture === 'large') {
            fetchData();
        } else {
            setUserInfo({
                id: authData.user?.id,
                rank: authData.user?.rank,
                username: authData.user?.firstName + " " + authData.user?.lastName,
                profilePicture: authData.user?.profile_picture,
                points: authData.user?.score,
                favoriteNFLTeam: authData.user?.favorite_nfl_team,
                favoriteNBATeam: authData.user?.favorite_nba_team
            });
        }

        return () => {
            document.body.classList.remove("account-page-bg");
        };
    }, [
        viewID,
        authData.user?.id,
        authData.user?.profile_picture,
        authData.user?.favorite_nba_team,
        authData.user?.favorite_nfl_team,
        authData.user?.firstName,
        authData.user?.lastName,
        authData.user?.rank,
        authData.user?.score
    ]);

    const handleProfilePictureChange = async (e) => {
        const token = localStorage.getItem('token');
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('profilePicture', file);
        // For this revised version, we no longer worry about file size – we simply upload.
        try {
            const response = await axios.post(`${API_BASE}/user/update-profile-picture`, formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });
            updateUserAttribute('profile_picture', response.data.profile_picture);
            setUserInfo({ ...userInfo, profilePicture: response.data.profile_picture });
            setProfilePicError('');
        } catch (error) {
            console.error('Error:', error.response?.data || error);
            setProfilePicError('Failed to update profile picture. Please try again.');
        }
    };


    const handleSelectFavoriteNFLTeam = async (e) => {
        const token = localStorage.getItem('token');
        const selectedTeam = e.target.value;
        setUserInfo(prev => ({ ...prev, favoriteNFLTeam: selectedTeam }));

        try {
            const response = await axios.post(`${API_BASE}/user/update-favorite-nfl-team`, {
                selectedTeam
            }, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            if (response.status === 200) {
                updateUserAttribute('favorite_nfl_team', response.data.favorite_nfl_team);
            }
        } catch (error) {
            console.error('Error:', error.response?.data || error);
        }
    };

    const handleSelectFavoriteNBATeam = async (e) => {
        const token = localStorage.getItem('token');
        const selectedTeam = e.target.value;
        setUserInfo(prev => ({ ...prev, favoriteNBATeam: selectedTeam }));

        try {
            const response = await axios.post(`${API_BASE}/user/update-favorite-nba-team`, {
                selectedTeam
            }, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            if (response.status === 200) {
                updateUserAttribute('favorite_nba_team', response.data.favorite_nba_team);
            }
        } catch (error) {
            console.error('Error:', error.response?.data || error);
        }
    };

    // Component to display profile picture from image url
    const UserProfilePicture = ({ src, style }) => {
        const defaultStyle = {
            width: '300px',
            height: '300px',
            borderRadius: '50%',
            objectFit: 'cover',
            margin: 'auto',
            display: 'block'
        };
        return <img src={src || '/default.jpg'} alt="User Profile" style={{ ...defaultStyle, ...style }} />;
    };

    return (
        <BaseLayout>
            <div className="account-page">
                <div className="user-info-box">
                    <div className="profile-section" style={{ position: 'relative' }}>
                        {loading ? (
                            <p>Loading Profile...</p>
                        ) : (
                            <UserProfilePicture src={userInfo.profilePicture} />
                        )}
                        <div className="profile-tags" style={{ textAlign: 'center', marginTop: '10px' }}>
                            {(((viewID === '' && [1,4,5,13,22].includes(authData.user?.id)) ||
                              (viewID !== '' && [1,4,5,13,22].includes(userInfo.id))) ) && (
                                <span className="badge badge-info">Founder</span>
                            )}
                            {((viewID === '' && authData.user?.rank === 1) || (viewID !== '' && userInfo.rank === 1)) && (
                                <span className="badge badge-gold">Leaderboard: 1st</span>
                            )}
                            {((viewID === '' && authData.user?.rank === 2) || (viewID !== '' && userInfo.rank === 2)) && (
                                <span className="badge badge-silver">Leaderboard: 2nd</span>
                            )}
                            {((viewID === '' && authData.user?.rank === 3) || (viewID !== '' && userInfo.rank === 3)) && (
                                <span className="badge badge-bronze">Leaderboard: 3rd</span>
                            )}
                        </div>
                        <div className="profile-details" style={{ textAlign: 'center', marginTop: '1rem' }}>
                            <p className="info-name">{loading ? 'Loading Profile...' : (viewID === '' ? authData.user?.firstName + " " + authData.user?.lastName : userInfo.username)}</p>
                            <p className="info-label">Points: {loading ? '' : (viewID === '' ? authData.user?.score : userInfo.points)}</p>
                            <h3 className="info-title">Favorite Teams:</h3>
                            <ul className="subinfo-title" style={{ listStyleType: 'none', paddingLeft: 0 }}>
                                {loading ? (
                                    <li></li>
                                ) : (
                                    <>
                                        {((viewID === '' || viewID === authData.user?.id)) && authData.user?.favorite_nfl_team && (
                                            <li>{authData.user?.favorite_nfl_team} (NFL)</li>
                                        )}
                                        {((viewID === '' || viewID === authData.user?.id)) && authData.user?.favorite_nba_team && (
                                            <li>{authData.user?.favorite_nba_team} (NBA)</li>
                                        )}
                                        {(!authData.user?.favorite_nfl_team && !authData.user?.favorite_nba_team && viewID === '') && (
                                            <li>(You have not chosen any favorite teams)</li>
                                        )}
                                        {(viewID !== '' && viewID !== authData.user?.id) && userInfo.favoriteNFLTeam && (
                                            <li>{userInfo.favoriteNFLTeam} (NFL)</li>
                                        )}
                                        {(viewID !== '' && viewID !== authData.user?.id) && userInfo.favoriteNBATeam && (
                                            <li>{userInfo.favoriteNBATeam} (NBA)</li>
                                        )}
                                        {(viewID !== '' && !userInfo.favoriteNFLTeam && !userInfo.favoriteNBATeam) && (
                                            <li>({userInfo.username} has not chosen any favorite teams)</li>
                                        )}
                                    </>
                                )}
                            </ul>
                        </div>
                    </div>

                    {viewID === '' && (
                        <div className="actions-wrapper" style={{ display: 'flex', justifyContent: 'center', marginTop: '2rem' }}>
                            <div className="actions" style={{ textAlign: 'center' }}>
                                {profilePicError && (
                                    <div className="alert alert-danger" role="alert">
                                        {profilePicError}
                                    </div>
                                )}
                                <div className="profile-picture-upload" style={{ marginBottom: '20px', width: '300px' }}>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        id="profile-picture-input"
                                        onChange={handleProfilePictureChange}
                                        style={{ display: 'none' }}
                                    />
                                    <label htmlFor="profile-picture-input" className="file-button" style={{ cursor: 'pointer' }}>Change Profile Picture</label>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <select
                                        className="file-button"
                                        onChange={handleSelectFavoriteNFLTeam}
                                        style={{ marginBottom: '20px', width: '300px' }}
                                        value={userInfo.favoriteNFLTeam}
                                    >
                                        <option value="" disabled>Select Favorite NFL Team</option>
                                        {NFL_TEAMS.map(team => (
                                            <option key={team} value={team}>{team}</option>
                                        ))}
                                    </select>
                                    <select
                                        className="file-button"
                                        onChange={handleSelectFavoriteNBATeam}
                                        style={{ marginBottom: '20px', width: '300px' }}
                                        value={userInfo.favoriteNBATeam}
                                    >
                                        <option value="" disabled>Select Favorite NBA Team</option>
                                        {NBA_TEAMS.map(team => (
                                            <option key={team} value={team}>{team}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </BaseLayout>
    );
};

export default AccountPage;